﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { FETCH_SPECIES_SUCCESS, INITIAL_SPECIES_REQUEST } from "@/redux/types/maintainers/species.type";

import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { hasPermission, toast } from "@/services/Utils";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import SpeciesTableHead from "./SpeciesTableHead";
import SpeciesTableBody from "./SpeciesTableBody";
import SpeciesForm from "./SpeciesForm/SpeciesForm";
import SpeciesFilter from "./SpeciesFilter";
import { filters } from "./species.constants";

const Species = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { addToast } = useToasts();
	const navigate = useNavigate();

	const { search, estate_id } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to, customers, estates, categories } = useSelector((state) => state.maintainers_species);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [filter, setFilter] = useState({ ...filters });
	const [showFilter, setShowFilter] = useState(false);
	const [permissions, setPermissions] = useState([]);
	const [estateParents, setEstateParents] = useState([]);

	const [showDelete, setShowDelete] = useState(false);

	useEffect(() => {
		dispatch({ type: INITIAL_SPECIES_REQUEST });
		api.get("/admin/species", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, estate_id, order, orderType, paginate, page, filter]);

	const updateData = (response) => {
		const { species, categories, customers, estates_parents, estates, permissions: allPermissions } = response;
		dispatch({
			type: FETCH_SPECIES_SUCCESS,
			payload: {
				species: species.data,
				last_page: species.last_page,
				from: species.from,
				to: species.to,
				quantity: species.total,
				customers: customers,
				estates: estates,
				categories: categories,
			},
		});
		setPermissions(allPermissions);
		setEstateParents(estates_parents);
		setLoaded(true);
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_SPECIES_REQUEST });
		api.post("/admin/species/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			setSelected("");
			toast(addToast, "Registro eliminado correctamente", "success");
			updateData(response);
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/species/save", {
			...form,
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
			modalFetching(false);
			afterSave();
		});
	};

	const applyFilter = (params) => {
		let new_filter = { ...filter };

		if (params.customerFilter !== filter.filter_customer) {
			new_filter = {
				...new_filter,
				filter_customer: params.customerFilter,
			};
		}
		if (params.estateFilter !== filter.filter_estate) {
			new_filter = { ...new_filter, filter_estate: params.estateFilter };
		}

		if (params.categoryFilter !== filter.filter_category) {
			new_filter = {
				...new_filter,
				filter_category: params.categoryFilter,
			};
		}

		setFilter(new_filter);
		setShowFilter(false);
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title="Paso 1"
				button={hasPermission("create", permissions) ? true : false}
				button_text={"Nuevo Registro"}
				action={() => navigate("/configuracion/paso-1/crear")}
				button_filter={true}
				back_url={"/configuracion?section=businessCenter"}
			/>

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					{showFilter && <SpeciesFilter filter={filter} applyFilter={applyFilter} />}

					<Col md={showFilter ? 9 : 12} className={classes.main_col}>
						<Row className={classes.header}>
							<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} />
						</Row>

						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<SpeciesTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />
									<SpeciesTableBody handleDelete={handleDelete} permissions={permissions} />
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />
					<SpeciesForm
						customers={customers}
						estates_parents={estateParents}
						estates={estates}
						categories={categories}
						confirmSave={confirmSave}
						permissions={permissions}
					/>
				</>
			)}
		</>
	);
};

export default Species;
